<template lang="pug">
div
  #form.new-edit-form(v-if="!dataHasLoaded")
    .form-card
      .spinner-container.form-spinner
        q-spinner(color="primary", size="3em")
  .user-new-form.justify-center(v-if="dataHasLoaded")
    handle-form(
      v-if="refetchReady && formData.form_header",
      @close-form="closeForm",
      :parentData="handleFormParentData"
    )
    .new-edit-form(v-if="!refetchReady")
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, computed } from "vue";
import { useStore } from "@/store";
import { handleError } from "@/services/handleErrors";
import { useEmitter } from "@/services/useEmitter";
import { useRoute } from "@/routing/router";
import { currentUser, currentUserId } from "@/services/useAuth";
import { removeMissingFormFields } from "@/services/useFormSchemaFields";
import backend from "@/api";
import HandleForm from "@/components/shared/forms/Form";
import _ from "lodash";

const store = useStore();
const emitter = useEmitter();
const route = useRoute();

const props = defineProps({
  parentData: { type: Object, default: () => {} },
});

const emit = defineEmits(["close-form"]);

const path = ref("/api/v3/dynamic/issues");
const grid = ref(props.parentData.grid);
const callbackParams = ref(props.parentData.callback_params || {});
const formParams = ref(props.parentData.params || {});
const formData = ref([]);
const dataHasLoaded = ref(false);
const refetchReady = ref(true);

const normalizeId = id => {
  return id === undefined || id === null || id === "" ? undefined : Number(id);
};

const explicitFacilityId = computed(() => {
  const id = formParams.value.facility_id ?? props.parentData.row?.facility_id ?? route.params.facility_id;

  return normalizeId(id);
});

const facilityId = computed(() => {
  return explicitFacilityId.value ?? normalizeId(currentUser.value?.facility_ids?.[0]);
});
const isCopyMode = computed(() => formParams.value.mode === "copy" && formParams.value.sourceId);
const submitFacilityId = computed(() => (isCopyMode.value ? explicitFacilityId.value : facilityId.value));
const additionalParams = computed(() => {
  return submitFacilityId.value ? { facility_id: submitFacilityId.value } : {};
});
const handleFormParentData = computed(() => ({
  method: "create",
  item: "issue",
  form_title: formData.value.form_header.title.add,
  title_icon_path: "add_header.svg",
  path: path.value,
  grid: grid.value,
  data: formData.value,
  additional_params: additionalParams.value,
  requestUserId: currentUserId.value,
  notifyOpenIssueEvent: props.parentData.notifyOpenIssueEvent || "open-created-dynamic-issue",
}));

const closeForm = data => {
  emit("close-form", data);
};

const refetchAndReformat = async param => {
  const formDataPath = `${path.value}/form_data`;
  const params = { [param.name]: param.value, facility_id: facilityId.value, request_user_id: currentUserId.value };

  try {
    refetchReady.value = false;

    const { data } = await backend.index(formDataPath, { params });
    const previousValues = _.cloneDeep(store.state.grid[grid.value]?.form || {});

    // фильтруем поля, у которых есть дети, выводим массив их имен
    const dependants = formData.value.fields
      .filter(elem => !!elem.watch?.parents?.find(el => el.parent === param.name))
      ?.map(el => el.name);

    dependants.forEach(el => store.commit("resetFormField", { grid_name: grid.value, field: el }));

    formData.value = data;
    removeMissingFormFields({ store, gridName: grid.value, formData: formData.value });

    const previousKeys = Object.keys(previousValues);

    if (previousKeys.length > 0) {
      restoreUserValues(previousKeys, previousValues, dependants);
    }

    refetchReady.value = true;
  } catch (e) {
    await handleError(e);
  }
};

const restoreUserValues = (previousKeys, previousValues, dependants = []) => {
  previousKeys.forEach(key => {
    if (dependants.includes(key)) return;
    formData.value.fields.forEach((field, index) => {
      if (field["name"] === key) {
        const prev = previousValues[key];
        if (prev && prev.field) {
          formData.value.fields[index].value = prev.field.value !== undefined ? prev.field.value : prev.field;
        }
      }
    });
  });
};

const loadFormData = async () => {
  const formDataPath = isCopyMode.value ? `${path.value}/${formParams.value.sourceId}/copy` : `${path.value}/form_data`;
  const params = isCopyMode.value ? {} : { facility_id: facilityId.value, request_user_id: currentUserId.value };

  try {
    const response = await backend.index(formDataPath, { params });
    formData.value = response.data?.data || response.data;

    const callbackKeys = Object.keys(callbackParams.value);

    if (callbackKeys.length > 0) {
      callbackKeys.forEach(key => {
        formData.value.fields.forEach(field => {
          if (field["name"] === key) {
            field["value"] = callbackParams.value[key];
          }
        });
      });
    }
  } catch (error) {
    await handleError(error);
    closeForm({});
  } finally {
    dataHasLoaded.value = true;
  }
};

onMounted(async () => {
  emitter.on("changeFormByField", refetchAndReformat);
  await loadFormData();
});

onBeforeUnmount(() => {
  emitter.off("changeFormByField", refetchAndReformat);
});
</script>

<script>
export default {
  name: "New",
};
</script>
